
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticPropsContext } from 'next';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import React from 'react';
import { Completion } from 'types';
import { motion } from 'framer-motion';
import { StarIcon } from '@heroicons/react/solid';
import { CameraIcon, ColorSwatchIcon, LightningBoltIcon, HomeIcon, FilmIcon, } from '@heroicons/react/outline';
const Steps = dynamic(() => import('@/components/atoms/Steps'));
const CallToAction = dynamic(() => import('@/components/CallToAction'));
const Footer = dynamic(() => import('@/components/layout/Footer'));
const Seo = dynamic(() => import('@/components/Seo'));
const RoomSpecificFAQ = dynamic(() => import('@/components/RoomSpecificFAQ'));
const Testimonials = dynamic(() => import('@/components/Testimonials'), {
    ssr: false,
});
const FaqTemplate = dynamic(() => import('@/components/FaqTemplate'));
const LaurelBackground = ({ className, style, }: {
    className?: string;
    style?: React.CSSProperties;
}) => (<svg viewBox='0 0 2553 801.7' className={className} style={style} fill='currentColor' xmlns='http://www.w3.org/2000/svg'>
    <path d='m334.46 49.002c-18.278 12.277-38.479 27.48-54.76 44.437-28.887 30.086-57.536 64.004-82.159 97.941-16.634 22.926-38.215 57.715-50.006 83.305-15.691 34.056-24.985 57.915-33.597 95.332-12.132 52.713-10.459 106.81 1.863 157.81 18.129 75.031 76.15 137.81 134.13 188.77 45.358 39.863 106.34 65.942 159.92 85.098l5.5363-14.63c-53.09-18.97-109.36-44.31-152.83-82.56-55.92-49.21-107.57-109.22-129.85-180.73-13.57-43.53-16.72-91.29-9.6-137.54 6.2375-40.516 21.918-81.121 37.193-118.04 12.558-30.349 27.2-53.991 45.06-78.892 22.645-31.574 47.529-62.365 73.981-90.48 17.123-18.199 40.023-34.629 58.415-46.982z'/>
    <path d='m311.24 756.98s-37.634-11.898-67.5-8.3929c-47.415 5.5642-51.786 35-51.786 35s19.022 11.809 59.821 7.3214c47.796-5.2574 59.464-33.929 59.464-33.929z'/>
    <path d='m247.15 713.02s-31.716-15.044-61.786-14.821c-52.058 0.38562-55.179 28.036-55.179 28.036s18.843 13.595 60.536 13.036c48.08-0.64498 56.429-26.25 56.429-26.25z'/>
    <path d='m199.74 666.79s-30.157-26.43-59.326-33.739c-50.498-12.653-67.224 16.73-67.224 16.73s18.771 22.52 59.277 32.411c46.711 11.406 67.273-15.402 67.273-15.402z'/>
    <path d='m146.2 595.58s-25.79-27.185-61.094-38.032c-46.073-14.156-67.729 13.636-68.361 14.331 0 0 18.879 23.762 58.267 34.179 52.832 13.973 71.188-10.478 71.188-10.478z'/>
    <path d='m120.7 538.91s-24.019-36.576-59.844-53.84c-34.801-16.78-60.225-2.49-60.856-1.93 0 0 17.877 32.67 50.767 48.325 44.687 21.271 69.938 7.4411 69.938 7.4412z'/>
    <path d='m112.96 489.18c-12.82-12.06-13.775-44.84-32.099-71.44-25.563-37.11-67.73-34.42-68.361-34.07 0 0 4.5142 46.234 36.481 74.178 33.973 29.697 63.978 31.335 63.978 31.335z'/>
    <path d='m109.42 411.25s1.2445-40.185-9.3525-70.701c-14.783-42.569-55.138-50.926-55.839-50.759 0 0-6.5444 45.519 16.848 80.952 24.861 37.657 48.344 40.507 48.344 40.507z'/>
    <path d='m126.51 335.94s8.0495-33.735 3.9716-65.78c-6.1462-48.297-43.73-58.521-44.448-58.451 0 0-12.884 44.733 5.4449 83.031 19.479 40.702 35.031 41.2 35.031 41.2z'/>
    <path d='m159.62 255.08s13.089-30.516 11.412-62.775c-2.3279-44.771-32.665-63.685-33.385-63.711 0 0-29.251 26.374-12.451 83.145 10.191 34.437 34.425 43.342 34.425 43.342z'/>
    <path d='m201.74 188.6s19.451-29.962 24.832-61.814c5.0517-29.903-7.0803-62.481-9.7885-65.475 0 0-31.65 13.91-30.465 67.294 0.79711 35.905 15.422 59.995 15.422 59.995z'/>
    <path d='m253.49 124.45s23.192-24.697 29.233-58.466c5-27.984-0.04-58.394-5.31-65.984 0 0-30.25 18.795-32.448 66.327-1.6587 35.875 6.1262 52.95 8.5253 58.127z'/>
    <path d='m291.03 85.107s44.199 1.5969 66.166-11.157c24.589-14.276 48.719-45.263 50.623-54.309 0 0-38.712-16.975-74.806 14.032-27.242 23.402-39.862 46.137-41.983 51.434z'/>
    <path d='m242.33 138.43s21.141-24.752 57.469-31.147c28.002-4.9287 49.529-0.37797 63.35 7.6753 0 0-21.322 27.318-62.19 31.084-35.762 3.2949-53.376-5.3854-58.63-7.6124z'/>
    <path d='m198.64 195.5s20.13-23.742 56.459-30.136c28.002-4.9287 41.448 0.12711 55.269 8.1804 0 0-17.029 26.056-57.897 29.821-35.762 3.2949-48.578-5.638-53.832-7.8649z'/>
    <path d='m156.23 269.95s18.805-29.262 52.808-43.562c34.843-14.654 52.739-7.6569 67.725-2.0626-4.7791 7.1686-24.337 37.05-56.046 46.02-34.557 9.7763-58.932 0.90881-64.487-0.39556z'/>
    <path d='m131.06 336.07s16.664-30.532 49.556-47.228c33.706-17.109 52.056-11.41 67.404-6.9021-4.254 7.4921-21.624 38.696-52.61 49.912-33.77 12.223-58.716 5.1223-64.35 4.2186z'/>
    <path d='m117.84 414.65c14.421-18.489 22.261-38.366 44.449-58.149 27.336-24.373 50.792-16.603 66.711-15.029-7.8365 14.719-19.603 38.003-43.92 55.546-29.126 21.011-61.536 17.473-67.24 17.633z'/>
    <path d='m119.63 487.05c12.262-19.986 18.081-35.565 37.916-57.707 24.437-27.278 47.605-22.435 63.6-22.652-6.1404 15.503-16.49 38.695-38.692 58.849-26.592 24.138-57.175 20.713-62.824 21.51z'/>
    <path d='m135.4 544.78c18.448-28.481 16.12-34.927 34.176-58.542 19.392-25.362 43.885-24.665 59.811-26.165-4.8772 15.946-12.165 37.517-32.837 59.237-22.045 23.163-44.975 20.181-61.149 25.47z'/>
    <path d='m165.71 601.35c11.63-25.198 9.6219-40.933 20.286-58.794 17.442-29.213 34.541-30.474 50.467-31.973-12.703 36.396-9.762 36.564-23.746 58.732-13.607 21.571-32.096 24.474-47.007 32.036z'/>
    <path d='m202.25 648.92c6.628-26.95-0.10184-35.197 6.973-54.76 11.571-31.996 28.119-36.484 43.469-40.983-1.7651 21.479 0.89983 35.106-11.139 58.387-8.5015 16.44-26.101 27.098-39.303 37.356z'/>
    <path d='m275.9 596.01s13.293 25.664 8.8773 55.408c-6.2559 42.144-28.777 48.458-28.777 48.458s-12.827-19.155-11.523-58.022c1.3924-41.516 31.423-45.845 31.423-45.845z'/>
    <path d='m323.88 638.94s13.303 25.984 10.393 55.914c-3.7305 38.356-24.736 47.953-24.736 47.953s-13.232-21.412-13.796-53.728c-0.86041-49.346 28.14-50.138 28.14-50.138z'/>
    <path d='M 2217.186 49.002 C 2235.464 61.279 2255.665 76.482 2271.946 93.439 C 2300.833 123.525 2329.482 157.443 2354.105 191.38 C 2370.739 214.306 2392.32 249.095 2404.111 274.685 C 2419.802 308.741 2429.096 332.6 2437.708 370.017 C 2449.84 422.73 2448.168 476.827 2435.845 527.827 C 2417.716 602.858 2359.695 665.637 2301.715 716.597 C 2256.357 756.46 2195.375 782.539 2141.795 801.695 L 2136.295 787.075 C 2189.393 768.093 2245.665 742.756 2289.135 704.503 C 2345.052 655.3 2396.695 595.293 2418.985 523.773 C 2432.551 480.242 2435.701 432.478 2428.582 386.233 C 2422.345 345.717 2406.664 305.112 2391.389 268.193 C 2378.831 237.844 2364.189 214.202 2346.329 189.301 C 2323.684 157.727 2298.8 126.936 2272.348 98.821 C 2255.225 80.622 2232.325 64.192 2213.933 51.839 L 2217.186 49.002 Z'/>
    <path d='M 2240.486 756.98 C 2240.486 756.98 2278.12 745.082 2307.986 748.587 C 2355.401 754.151 2359.772 783.587 2359.772 783.587 C 2359.772 783.587 2340.75 795.396 2299.95 790.909 C 2252.154 785.651 2240.486 756.98 2240.486 756.98 Z'/>
    <path d='M 2304.486 713.02 C 2304.486 713.02 2336.202 697.976 2366.272 698.199 C 2418.33 698.585 2421.451 726.235 2421.451 726.235 C 2421.451 726.235 2402.608 739.83 2360.915 739.271 C 2312.835 738.626 2304.486 713.021 2304.486 713.021 L 2304.486 713.02 Z'/>
    <path d='M 2351.986 666.79 C 2351.986 666.79 2382.143 640.36 2411.312 633.051 C 2461.81 620.398 2478.536 649.781 2478.536 649.781 C 2478.536 649.781 2459.765 672.301 2419.259 682.192 C 2372.548 693.598 2351.986 666.79 2351.986 666.79 Z'/>
    <path d='M 2405.486 595.58 C 2405.486 595.58 2431.276 568.395 2466.58 557.548 C 2512.653 543.392 2534.309 571.184 2534.941 571.879 C 2534.941 571.879 2516.062 595.641 2476.675 606.058 C 2423.843 620.031 2405.487 595.58 2405.487 595.58 L 2405.486 595.58 Z'/>
    <path d='M 2430.986 538.91 C 2430.986 538.91 2455.005 502.334 2490.83 485.07 C 2525.636 468.296 2551.059 482.582 2551.691 483.144 C 2551.691 483.144 2533.814 515.814 2500.924 531.469 C 2456.237 552.74 2430.986 538.91 2430.986 538.91 Z'/>
    <path d='M 2438.686 489.18 C 2451.503 477.121 2452.46 444.342 2470.784 417.739 C 2496.347 380.628 2538.513 383.32 2539.144 383.667 C 2539.144 383.667 2534.63 429.901 2502.663 457.845 C 2468.69 487.542 2438.685 489.18 2438.685 489.18 L 2438.686 489.18 Z'/>
    <path d='M 2442.286 411.25 C 2442.286 411.25 2441.042 371.065 2451.639 340.549 C 2466.422 297.98 2506.777 289.623 2507.478 289.79 C 2507.478 289.79 2514.022 335.309 2490.63 370.742 C 2465.77 408.399 2442.286 411.249 2442.286 411.249 L 2442.286 411.25 Z'/>
    <path d='M 2425.186 335.94 C 2425.186 335.94 2417.137 302.205 2421.215 270.16 C 2427.361 221.863 2464.945 211.639 2465.663 211.709 C 2465.663 211.709 2478.547 256.442 2460.218 294.74 C 2440.739 335.442 2425.187 335.94 2425.187 335.94 L 2425.186 335.94 Z'/>
    <path d='M 2392.086 255.08 C 2392.086 255.08 2378.997 224.564 2380.674 192.305 C 2383.002 147.534 2413.339 128.62 2414.059 128.594 C 2414.059 128.594 2443.31 154.968 2426.51 211.739 C 2416.319 246.176 2392.085 255.081 2392.085 255.081 L 2392.086 255.08 Z'/>
    <path d='M 2349.886 188.6 C 2349.886 188.6 2330.435 158.638 2325.054 126.786 C 2320.003 96.883 2332.135 64.305 2334.843 61.311 C 2334.843 61.311 2366.493 75.221 2365.308 128.605 C 2364.511 164.51 2349.886 188.6 2349.886 188.6 Z'/>
    <path d='M 2298.186 124.45 C 2298.186 124.45 2274.994 99.753 2268.953 65.984 C 2263.946 37.995 2268.987 7.585 2274.264 -0.005 C 2274.264 -0.005 2304.514 18.79 2306.712 66.322 C 2308.37 102.197 2300.585 119.272 2298.186 124.449 L 2298.186 124.45 Z'/>
    <path d='M 2260.686 85.107 C 2260.686 85.107 2216.487 86.704 2194.52 73.95 C 2169.931 59.674 2145.801 28.687 2143.897 19.641 C 2143.897 19.641 2182.609 2.666 2218.703 33.673 C 2245.945 57.075 2258.565 79.81 2260.686 85.107 Z'/>
    <path d='M 2309.386 138.43 C 2309.386 138.43 2288.245 113.678 2251.917 107.283 C 2223.915 102.354 2202.388 106.905 2188.567 114.958 C 2188.567 114.958 2209.889 142.276 2250.757 146.042 C 2286.519 149.337 2304.133 140.657 2309.387 138.43 L 2309.386 138.43 Z'/>
    <path d='M 2352.986 195.5 C 2352.986 195.5 2332.856 171.758 2296.527 165.364 C 2268.525 160.435 2255.079 165.491 2241.258 173.544 C 2241.258 173.544 2258.287 199.6 2299.155 203.365 C 2334.917 206.66 2347.733 197.727 2352.987 195.501 L 2352.986 195.5 Z'/>
    <path d='M 2395.486 269.95 C 2395.486 269.95 2376.681 240.688 2342.678 226.388 C 2307.835 211.734 2289.939 218.731 2274.953 224.325 C 2279.732 231.494 2299.29 261.375 2330.999 270.345 C 2365.556 280.122 2389.931 271.254 2395.486 269.95 Z'/>
    <path d='M 2420.586 336.07 C 2420.586 336.07 2403.922 305.538 2371.03 288.842 C 2337.324 271.733 2318.974 277.432 2303.626 281.94 C 2307.88 289.432 2325.25 320.636 2356.236 331.852 C 2390.006 344.075 2414.952 336.974 2420.586 336.071 L 2420.586 336.07 Z'/>
    <path d='M 2433.786 414.65 C 2419.365 396.161 2411.525 376.284 2389.337 356.501 C 2362.001 332.128 2338.545 339.898 2322.626 341.472 C 2330.463 356.191 2342.229 379.475 2366.546 397.018 C 2395.672 418.029 2428.082 414.491 2433.786 414.651 L 2433.786 414.65 Z'/>
    <path d='M 2432.086 487.05 C 2419.824 467.064 2414.005 451.485 2394.17 429.343 C 2369.733 402.065 2346.565 406.908 2330.57 406.691 C 2336.711 422.194 2347.06 445.386 2369.262 465.54 C 2395.854 489.678 2426.436 486.253 2432.086 487.05 Z'/>
    <path d='M 2416.286 544.78 C 2397.838 516.299 2400.166 509.853 2382.11 486.238 C 2362.718 460.876 2338.225 461.573 2322.299 460.073 C 2327.176 476.019 2334.465 497.59 2355.136 519.31 C 2377.181 542.473 2400.111 539.491 2416.285 544.78 L 2416.286 544.78 Z'/>
    <path d='M 2385.986 601.35 C 2374.356 576.152 2376.364 560.417 2365.7 542.556 C 2348.258 513.343 2331.159 512.082 2315.233 510.583 C 2327.936 546.979 2324.995 547.147 2338.979 569.315 C 2352.586 590.886 2371.075 593.789 2385.986 601.351 L 2385.986 601.35 Z'/>
    <path d='M 2349.386 648.92 C 2342.758 621.97 2349.488 613.723 2342.413 594.16 C 2330.842 562.164 2314.294 557.676 2298.944 553.177 C 2300.709 574.656 2298.044 588.283 2310.083 611.564 C 2318.585 628.004 2336.184 638.662 2349.386 648.92 Z'/>
    <path d='M 2275.786 596.01 C 2275.786 596.01 2262.493 621.674 2266.909 651.418 C 2273.165 693.562 2295.686 699.876 2295.686 699.876 C 2295.686 699.876 2308.513 680.721 2307.209 641.854 C 2305.816 600.338 2275.786 596.009 2275.786 596.009 L 2275.786 596.01 Z'/>
    <path d='M 2227.786 638.94 C 2227.786 638.94 2214.483 664.924 2217.394 694.854 C 2221.125 733.21 2242.13 742.807 2242.13 742.807 C 2242.13 742.807 2255.362 721.395 2255.926 689.079 C 2256.787 639.733 2227.786 638.941 2227.786 638.941 L 2227.786 638.94 Z'/>
  </svg>);
import { getPublicCompletions } from '@/data/completions';
import { RoomThemes } from '@/components/landing/RoomGPT/RoomThemes';
import { RoomTypes } from '@/components/landing/RoomGPT/RoomTypes';
import { MultiTurnEditingSection } from '@/components/landing/RoomGPT/MultiTurnEditingSection';
import { TakePhotoSection } from '@/components/landing/RoomGPT/TakePhotoSection';
import { ThreeDFlythrough } from '@/components/landing/RoomGPT/ThreeDFlythrough';
import { SketchToRenderSection } from '@/components/landing/RoomGPT/SketchToRenderSection';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import LogosCloud from '@/components/LogosCloud';
import { HAS_FREE_PLAN } from '@/constants/common';
import { Preset, PresetCategory, presets } from '@/constants/presets';
import { roomTypesList } from '@/constants/roomTypes';
const STAR_INDICES = [0, 1, 2, 3, 4];
const HERO_FEATURES = [
    { icon: CameraIcon, textKey: 'hero-feature-photo' },
    { icon: ColorSwatchIcon, textKey: 'hero-feature-styles' },
    { icon: LightningBoltIcon, textKey: 'hero-feature-sketch' },
    { icon: HomeIcon, textKey: 'hero-feature-staging' },
    { icon: FilmIcon, textKey: 'hero-feature-3d' },
] as const;
interface TemplateProps {
    completions: Completion[];
    preset: Preset;
    locale?: string;
    otherPresets: Preset[];
    roomType: (typeof roomTypesList)[0];
}
const RoomType = ({ completions = [], preset, locale = 'en', otherPresets, roomType, }: TemplateProps) => {
    const router = useRouter();
    const { room } = router.query;
    const { t, lang } = useLocale('ai-interior');
    if (!roomType)
        return null;
    // Check if room has specific FAQs
    const hasRoomSpecificFaqs = roomType.faqs && roomType.faqs.length > 0;
    return (<main className='bg-gray-100 dark:bg-slate-900'>
      <Seo templateTitle={t('room-design-generator-seo-title', {
            room: t(roomType.name),
        })} description={t('room-design-generator-seo-description', {
            room: t(roomType.name),
        })} image={`https://media.easy-peasy.ai/room-types/${roomType.slug}.jpeg`}/>
      <div className='relative isolate overflow-hidden bg-white dark:bg-slate-900'>
        <LandingPageNavigation />

        {/* Animated mesh gradient background */}
        <div className='absolute inset-0 -z-10 overflow-hidden'>
          <div className='absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px]'></div>
          <motion.div animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 10, 0],
        }} transition={{ duration: 20, repeat: Infinity, ease: 'linear' }} className='absolute -top-[40%] -right-[20%] h-[80vh] w-[80vh] rounded-full bg-gradient-to-br from-primary-400/20 via-purple-400/15 to-transparent blur-[100px]'/>
          <motion.div animate={{
            scale: [1, 1.15, 1],
            rotate: [0, -8, 0],
        }} transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'linear',
            delay: 5,
        }} className='absolute -bottom-[30%] -left-[20%] h-[70vh] w-[70vh] rounded-full bg-gradient-to-tr from-indigo-400/20 via-primary-400/10 to-transparent blur-[100px]'/>
        </div>

        <div className='mx-auto max-w-7xl px-6 pb-28 pt-14 sm:pb-36 lg:flex lg:items-center lg:gap-x-12 lg:px-8 lg:pb-40 2xl:max-w-[1440px]'>
          <div className='mx-auto max-w-2xl lg:mx-0 lg:w-[55%] lg:max-w-none lg:flex-shrink-0 2xl:w-[50%]'>
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className='mb-6 flex'>
              <div className='relative inline-flex flex-col items-center justify-center w-[200px] h-[64px]'>
                <LaurelBackground className='absolute inset-0 w-full h-full text-gray-900 dark:text-white'/>
                <span className='font-extrabold text-[15px] tracking-wide text-gray-900 dark:text-white whitespace-nowrap z-10 relative -top-[4px]'>
                  {t('#1 AI Interior App', {
            defaultValue: '#1 AI Interior App',
        })}
                </span>
                <div className='flex items-center mt-1 gap-1 z-10 relative -top-[4px]'>
                  {STAR_INDICES.map((i) => (<StarIcon key={i} className='w-[14px] h-[14px] text-gray-900 dark:text-white'/>))}
                </div>
              </div>
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className='text-5xl font-black tracking-tight text-gray-900 dark:text-white sm:text-6xl lg:text-[3.25rem] lg:leading-[1.08] xl:text-6xl xl:leading-[1.08]'>
              {t('room-design-generator-headline', {
            room: t(roomType.name),
        })}
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className='mt-6 text-lg leading-relaxed text-gray-600 dark:text-slate-300 xl:text-xl xl:leading-relaxed'>
              {t('room-design-generator-description', {
            room: t(roomType.name),
        })}
            </motion.p>

            <div className='mt-6 flex flex-wrap gap-2.5'>
              {HERO_FEATURES.map((feature, i) => (<motion.div key={feature.textKey} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 + i * 0.07 }} className='group flex items-center gap-2 rounded-full bg-white dark:bg-slate-800/60 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-slate-200 shadow-sm ring-1 ring-gray-200/80 dark:ring-white/10 hover:ring-primary-500/50 hover:shadow-md hover:shadow-primary-500/5 transition-all duration-300'>
                  <feature.icon className='h-4 w-4 text-primary-600 transition-transform duration-300 group-hover:scale-110'/>
                  <span>{t(feature.textKey)}</span>
                </motion.div>))}
            </div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.55 }} className='mt-10 flex items-center gap-x-6'>
              <ButtonLink href={HAS_FREE_PLAN
            ? `/login?preset=ai-interior-designer`
            : '/#pricing'} variant='dark' className='group relative overflow-hidden rounded-full px-8 py-3.5 text-base font-semibold shadow-xl shadow-gray-900/10 hover:shadow-2xl hover:shadow-gray-900/20 hover:scale-[1.03] active:scale-[0.98] transition-all duration-300'>
                <span className='relative flex items-center gap-2'>
                  {t('room-design-generator-cta', {
            room: t(roomType.name),
        })}
                  <svg className='h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5' fill='none' viewBox='0 0 24 24' strokeWidth='2.5' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' d='M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3'/>
                  </svg>
                </span>
              </ButtonLink>
              <a href='#before-after' className='group text-sm font-semibold leading-6 text-gray-900 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors'>
                {t('See examples')}{' '}
                <span aria-hidden='true' className='inline-block transition-transform duration-300 group-hover:translate-x-1'>
                  →
                </span>
              </a>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.92, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }} className='mx-auto mt-16 max-w-2xl sm:mt-24 lg:mt-0 lg:w-[45%] lg:flex-shrink-0 2xl:w-[50%]'>
            <div className='group relative rounded-2xl mr-4 bg-gray-900/5 dark:bg-white/5 p-3 ring-1 ring-inset ring-gray-900/10 dark:ring-white/10 lg:rounded-3xl lg:p-4 transition-transform duration-500 hover:scale-[1.02]'>
              {/* Glow effect behind image */}
              <div className='absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-br from-primary-400/20 via-purple-400/15 to-indigo-400/20 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100'/>
              <div className='relative overflow-hidden rounded-xl shadow-2xl'>
                {roomType.heroVideo ? (
        // Before -> after transformation loop. The static jpeg stays
        // as the poster, so the first paint, reduced-motion users
        // and the SEO image are unchanged.
        <video src={roomType.heroVideo} poster={`https://media.easy-peasy.ai/room-types/${roomType.slug}.jpeg`} autoPlay muted loop playsInline preload='metadata' width={1000} height={1000} aria-label={`${roomType.name} before and after AI redesign`} className='w-full rounded-xl object-cover shadow-2xl ring-1 ring-gray-900/10'/>) : (<Image width={1000} height={1000} src={`https://media.easy-peasy.ai/room-types/${roomType.slug}.jpeg`} alt={`AI-redesigned ${roomType.name.toLowerCase()}`} className='w-full rounded-xl object-cover shadow-2xl ring-1 ring-gray-900/10' priority/>)}
                <div className='absolute inset-0 ring-1 ring-inset ring-black/10 rounded-xl pointer-events-none'></div>
              </div>
            </div>
          </motion.div>
        </div>
        <LogosCloud />
      </div>
      <TakePhotoSection roomSlug={roomType.slug} roomName={roomType.name}/>
      <ThreeDFlythrough />
      <SketchToRenderSection />
      <MultiTurnEditingSection />
      <section className='bg-white dark:bg-slate-800'>
        <div className='mx-auto max-w-md px-4 py-8 text-center sm:max-w-3xl sm:px-6 sm:py-12 lg:max-w-7xl lg:px-8'>
          <motion.p initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} viewport={{ once: true }} className='mt-2 text-3xl font-black tracking-tight text-primary-600 sm:text-5xl'>
            {t(`It's easy to get started`)}
          </motion.p>
        </div>
        <div className='flex justify-center px-5 pt-10'>
          <Steps preset={preset}/>
        </div>
      </section>
      <RoomThemes />
      {lang === 'en' && hasRoomSpecificFaqs && roomType.faqs && (<RoomSpecificFAQ faqs={roomType.faqs} roomName={roomType.name}/>)}
      <Testimonials />
      <CallToAction preset='ai-interior-designer'/>
      <RoomTypes />
      <Footer />
    </main>);
};
export default RoomType;
const getStaticProps = async (ctx: GetStaticPropsContext) => {
    const locale = ctx.locale as string;
    const { room } = ctx.params as {
        room: string;
    };
    // Find the specific room type
    const roomType = roomTypesList.find((type) => type.slug === room);
    if (!roomType) {
        return { notFound: true };
    }
    // map es to Spanish, ja to Japanese
    let lang;
    switch (locale) {
        case 'es':
            lang = 'Spanish';
            break;
        case 'ja':
            lang = 'Japanese';
            break;
        case 'fr':
            lang = 'French';
            break;
        case 'it':
            lang = 'Italian';
            break;
        case 'de':
            lang = 'German';
            break;
        case 'ko':
            lang = 'Korean';
            break;
        case 'zh':
            lang = 'Chinese';
            break;
        case 'th':
            lang = 'Thai';
            break;
        case 'ar':
            lang = 'Arabic';
            break;
        case 'pt':
            lang = 'Portuguese';
            break;
        case 'ms':
            lang = 'Malay';
            break;
        default:
            lang = '';
    }
    console.log('lang', lang, locale);
    const completions = await getPublicCompletions('ai-interior-designer', lang);
    const preset = presets.find((preset) => preset.slug === 'ai-interior-designer');
    if (!preset) {
        return { notFound: true };
    }
    // do not include icon from preset, exclude it from the preset
    const { icon, ...presetWithoutIcon } = preset;
    const templateTitleWords = presetWithoutIcon.title
        .replace('Generator', '')
        .trim()
        .split(' ');
    const otherPresets = presets
        .filter((p) => (p.category === preset.category ||
        p.category.includes(p.category[0] as PresetCategory)) &&
        p.slug !== preset.slug &&
        !p.hideLandingPage)
        .map((preset) => {
        const { icon, ...presetWithoutIcon } = preset;
        return presetWithoutIcon;
    });
    const presetsWithTitleMatch = otherPresets
        .filter((preset) => templateTitleWords.some((word) => preset.title.includes(word)))
        .slice(0, 12);
    const remainingPresets = otherPresets
        .filter((preset) => !presetsWithTitleMatch.includes(preset))
        .slice(0, 12 - presetsWithTitleMatch.length);
    const finalPresets = [...presetsWithTitleMatch, ...remainingPresets];
    return {
        props: {
            completions,
            preset: presetWithoutIcon,
            locale,
            otherPresets: finalPresets,
            roomType,
        },
    };
};
// add static paths
export async function getStaticPaths() {
    const locales = [
        'en',
        'ja',
        'es',
        'fr',
        'it',
        'de',
        'ko',
        'zh',
        'th',
        'pt',
        'ar',
    ];
    const paths = roomTypesList.flatMap((roomType) => locales.map((locale) => ({
        params: { room: roomType.slug },
        locale,
    })));
    return {
        paths,
        fallback: false,
    };
}

    async function __Next_Translate__getStaticProps__1a0cbfb6591__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-interior-design-generator/[room]/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0cbfb6591__ as getStaticProps }
  